import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogTitle,
  Divider,
  Paper,
  DialogContent,
  CircularProgress,
} from '@mui/material';
import { Cancel, Save } from '@mui/icons-material';
import { farmService } from '../../FarmService';
import { customerService } from '../../../Customer/CustomerService';
import { Farm, Tenant } from '../../Model/FarmModel';
import { StorageAccount } from '../../../StorageAccount/Model/StorageAccountModel';
import { AzureSearchService } from '../../../SearchService/Model/AzureSearchServiceModel';
import { SqlServer } from '../../../SqlServer/Model/SqlServerModel';
import { CustomerV2 } from '../../../Customer/Model/CustomerModel';
import { AppserviceSkuDialog } from '../../../../Common/Dialog/AppserviceSkuDialog';
import { GetCustomerEditActionLink } from '../../../../../Common/CommonComponents';
import {
  ThemedLaunchIcon,
  ThemedWarningIcon,
} from '../../../../../Common/Components/Icons';
import { tenantStateIcons } from '../../../../../Common/Components/StateMappers/tenantStateIcons';
import {
  RemoveDuplicateAppserviceplans,
  createNewAppServicePlan,
} from '../../../../../Common/CommonMethods';
import StorageAccountConstants from '../../../StorageAccount/Common/Constants';
import AzureSearchServiceConstants from '../../../SearchService/Common/Constants';
import { MenuProps } from '../../../DashBoard/Common/CommonConstants';
import CustomButton, {
  CustomSelect,
} from '../../../../../Common/Components/Controls/InputControls';
import { tenantStateMapping } from '../../StateMapper/StateMapper';
import { areTenantsEqual, getTenantSubDomain } from './helper';
import { ReplicaSelect } from './ReplicaSelect';
import { showToast } from '../../../../../lib/toast';

type DialogState = {
  open: boolean;
  selectedTenantId: string;
  selectedValue: string;
};

const defaultDialogState: DialogState = {
  open: false,
  selectedTenantId: '',
  selectedValue: 'Not Selected',
};

type TenantTableProps = {
  farm: Farm;
  storageaccounts: StorageAccount[];
  azureSearchService: AzureSearchService[];
  sqlServers: SqlServer[];
  replicaServers: SqlServer[];
  searchQuery: string;
  dialogState: boolean;
  setDialogState: (val: boolean) => void;
};

interface TenantRowProps {
  tenant: Tenant;
  originalTenant?: Tenant;
  customers: CustomerV2[];
  appserviceplans: string[];
  storageaccounts: StorageAccount[];
  azureSearchService: AzureSearchService[];
  sqlServers: SqlServer[];
  replicaServers: SqlServer[];
  isMobile: boolean;
  onAppServicePlanChange: (tenantId: string, value: string) => void;
  onStorageAccountChange: (tenantId: string, value: string) => void;
  onAzureSearchServiceChange: (tenantId: string, value: string) => void;
  onSqlServerChange: (tenantId: string, value: string) => void;
  onReplicaChange: (tenant: Tenant, replicas: string[]) => void;
  onOpenDialog: (tenantId: string) => void;
}

// Extracted Row Component for Deep Memoization
const TenantRow = React.memo(
  ({
    tenant,
    originalTenant,
    customers,
    appserviceplans,
    storageaccounts,
    azureSearchService,
    sqlServers,
    replicaServers,
    isMobile,
    onAppServicePlanChange,
    onStorageAccountChange,
    onAzureSearchServiceChange,
    onSqlServerChange,
    onReplicaChange,
    onOpenDialog,
  }: TenantRowProps) => {
    const hasChanged = originalTenant
      ? !areTenantsEqual(tenant, originalTenant)
      : false;

    return (
      <TableRow hover>
        <TableCell sx={{ width: 48, p: 1, textAlign: 'center' }}>
          {hasChanged ? (
            <ThemedWarningIcon titleAccess="Not Saved" />
          ) : (
            <Tooltip title="Visit Home Page">
              <IconButton
                size="small"
                href={tenant.ApplicationUrl}
                target="_blank"
                disabled={!tenant.ApplicationUrl}
              >
                <ThemedLaunchIcon />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
        <TableCell sx={{ p: 1, textAlign: 'center' }}>
          <Tooltip title={tenantStateMapping[tenant.StatusID]}>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              {tenantStateIcons[tenant.StatusID]}
            </Box>
          </Tooltip>
        </TableCell>
        <TableCell sx={{ p: 1 }}>
          <GetCustomerEditActionLink
            id={tenant.Id}
            subdomain={getTenantSubDomain(customers, tenant.Id)}
          />
        </TableCell>
        <TableCell sx={{ p: 1 }}>{tenant.ProVersion ?? ''}</TableCell>
        <TableCell sx={{ p: 1 }}>
          <CustomSelect
            name={`Appserviceplan-${tenant.Id}`}
            size="small"
            sx={{
              width: isMobile ? '100%' : '180px',
              '& .MuiSelect-select': { py: 1, fontSize: '0.875rem' },
            }}
            value={tenant.AppservicePlan}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onAppServicePlanChange(tenant.Id, e.target.value)
            }
            MenuProps={MenuProps}
          >
            <MenuItem
              value="Create Appservice Plan"
              onClick={() => onOpenDialog(tenant.Id)}
              sx={{ fontWeight: 600, color: 'primary.main' }}
            >
              Create Appservice Plan
            </MenuItem>
            {appserviceplans.map((plan: string) => (
              <MenuItem key={plan} value={plan} sx={{ fontSize: '0.875rem' }}>
                {plan}
              </MenuItem>
            ))}
          </CustomSelect>
        </TableCell>
        <TableCell sx={{ p: 1 }}>
          <CustomSelect
            name={`StorageAccountName-${tenant.Id}`}
            size="small"
            sx={{
              width: isMobile ? '100%' : '180px',
              '& .MuiSelect-select': { py: 1, fontSize: '0.875rem' },
            }}
            value={tenant.StorageAccountName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onStorageAccountChange(tenant.Id, e.target.value)
            }
            MenuProps={MenuProps}
          >
            {storageaccounts.map((item: StorageAccount) => (
              <MenuItem
                key={item.Name}
                value={item.Name}
                sx={{ fontSize: '0.875rem' }}
              >
                {item.Name}
              </MenuItem>
            ))}
          </CustomSelect>
        </TableCell>
        <TableCell sx={{ p: 1 }}>
          <CustomSelect
            name={`AzureSearchServiceName-${tenant.Id}`}
            size="small"
            sx={{
              width: isMobile ? '100%' : '180px',
              '& .MuiSelect-select': { py: 1, fontSize: '0.875rem' },
            }}
            value={tenant.AzureSearchServiceName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onAzureSearchServiceChange(tenant.Id, e.target.value)
            }
            MenuProps={MenuProps}
          >
            {azureSearchService.map((item: AzureSearchService) => (
              <MenuItem
                key={item.Name}
                value={item.Name}
                sx={{ fontSize: '0.875rem' }}
              >
                {item.Name}
              </MenuItem>
            ))}
          </CustomSelect>
        </TableCell>
        <TableCell sx={{ p: 1 }}>
          <CustomSelect
            name={`SqlServer-${tenant.Id}`}
            size="small"
            sx={{
              width: isMobile ? '100%' : '180px',
              '& .MuiSelect-select': { py: 1, fontSize: '0.875rem' },
            }}
            value={tenant.AzureSQLServerName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onSqlServerChange(tenant.Id, e.target.value)
            }
            MenuProps={MenuProps}
          >
            {sqlServers.map((item: SqlServer) => (
              <MenuItem
                key={item.ServerName}
                value={item.ServerName}
                sx={{ fontSize: '0.875rem' }}
              >
                {item.ServerName}
              </MenuItem>
            ))}
          </CustomSelect>
        </TableCell>
        <TableCell sx={{ p: 1 }}>
          <ReplicaSelect
            tenant={tenant}
            replicaServers={replicaServers}
            isMobile={isMobile}
            onChange={(replicas: string[]) => onReplicaChange(tenant, replicas)}
          />
        </TableCell>
      </TableRow>
    );
  },
  (prevProps, nextProps) => {
    return (
      areTenantsEqual(prevProps.tenant, nextProps.tenant) &&
      prevProps.isMobile === nextProps.isMobile &&
      prevProps.appserviceplans === nextProps.appserviceplans
    );
  },
);

const TenantTable: React.FC<TenantTableProps> = ({
  farm,
  storageaccounts,
  azureSearchService,
  sqlServers,
  replicaServers,
  searchQuery,
  dialogState,
  setDialogState,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [loadingCount, setLoadingCount] = useState(0);
  const [originalTenants, setOriginalTenants] = useState<Tenant[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>(farm.Tenants);
  const [appserviceplans, setAppserviceplans] = useState<string[]>([]);
  const [customers, setCustomers] = useState<CustomerV2[]>([]);
  const [appserviceDialogState, setAppserviceDialogState] =
    useState<DialogState>(defaultDialogState);

  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);

  // Debounce the raw input so fast typing doesn't instantly block the thread
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 250);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Pre-calculate O(1) dictionary routing for customer names to avoid O(N^2) lookup death-loops
  const customerMap = useMemo(() => {
    const map = new Map<string, string>();
    customers.forEach((c) => {
      if (c.Id && c.CustomerName) map.set(c.Id, c.CustomerName.toLowerCase());
    });
    return map;
  }, [customers]);

  // Synchronous Filtering via useMemo
  const filteredTenants = useMemo(() => {
    if (!debouncedSearchQuery) return tenants;
    const lowerSearch = debouncedSearchQuery.toLowerCase();

    return tenants.filter((t) => {
      const customerName = customerMap.get(t.Id) || '';

      return (
        (t.ApplicationUrl?.toLowerCase() || '').includes(lowerSearch) ||
        (t.Id?.toLowerCase() || '').includes(lowerSearch) ||
        customerName.includes(lowerSearch)
      );
    });
  }, [debouncedSearchQuery, tenants, customerMap]);

  useEffect(() => {
    if (!dialogState) {
      setOriginalTenants([]);
      setTenants([]);
    }
  }, [dialogState]);

  const incrementLoading = () => setLoadingCount((prev) => prev + 1);
  const decrementLoading = () =>
    setLoadingCount((prev) => Math.max(prev - 1, 0));

  const loadInitialData = useCallback(async () => {
    try {
      incrementLoading();
      const [tenantsResponse, customersResponse, plansResponse] =
        await Promise.all([
          farmService.getTenantsOnFarm(farm.Id),
          customerService.getCustomers(),
          farmService.getAppservicePlans(farm.Id),
        ]);

      setOriginalTenants([...tenantsResponse.Items]);
      setTenants([...tenantsResponse.Items]);
      setCustomers(customersResponse.Items);
      setAppserviceplans(plansResponse);
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      decrementLoading();
    }
  }, [farm.Id]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const updateTenantProperty = useCallback(
    (tenantId: string, updates: Partial<Tenant>) => {
      setTenants((prev) =>
        prev.map((tenant) =>
          tenant.Id === tenantId ? { ...tenant, ...updates } : tenant,
        ),
      );
    },
    [],
  );

  const handleAppServicePlanChange = useCallback(
    (tenantId: string, value: string) => {
      updateTenantProperty(tenantId, { AppservicePlan: value });
    },
    [updateTenantProperty],
  );

  const handleSqlServerChange = useCallback(
    (tenantId: string, value: string) => {
      updateTenantProperty(tenantId, {
        AzureSQLServerName: value,
        Replicas: [],
      });
    },
    [updateTenantProperty],
  );

  const handleStorageAccountChange = useCallback(
    (tenantId: string, value: string) => {
      if (window.confirm(StorageAccountConstants.StorageAccoutChangeWarn)) {
        updateTenantProperty(tenantId, { StorageAccountName: value });
      }
    },
    [updateTenantProperty],
  );

  const handleAzureSearchServiceChange = useCallback(
    (tenantId: string, value: string) => {
      if (
        window.confirm(AzureSearchServiceConstants.AzureSearchServiceChangeWarn)
      ) {
        updateTenantProperty(tenantId, { AzureSearchServiceName: value });
      }
    },
    [updateTenantProperty],
  );

  const handleReplicaChange = useCallback(
    (tenant: Tenant, selectedReplicas: string[]) => {
      const uniqueReplicas = [...new Set(selectedReplicas)];

      if (uniqueReplicas.length > 3) {
        showToast({
          type: 'error',
          title:
            'More than 3 replicas are not allowed. Please unselect any other',
        });
        return;
      }
      updateTenantProperty(tenant.Id, { Replicas: uniqueReplicas });
    },
    [updateTenantProperty],
  );

  const handleOpenDialog = useCallback((tenantId: string) => {
    setAppserviceDialogState({
      open: true,
      selectedTenantId: tenantId,
      selectedValue: 'Not Selected',
    });
  }, []);

  const handleCloseDialog = (value: string) => {
    setAppserviceDialogState((prev) => ({ ...prev, open: false }));

    if (value !== 'Not Selected' && appserviceDialogState.selectedTenantId) {
      const newPlan = createNewAppServicePlan(
        appserviceplans,
        [value],
        farm.Id,
      );
      const planName = Array.isArray(newPlan) ? newPlan[0] : newPlan;
      const updatedPlans = RemoveDuplicateAppserviceplans([
        ...appserviceplans,
        planName,
      ]);
      setAppserviceplans(updatedPlans);

      updateTenantProperty(appserviceDialogState.selectedTenantId, {
        AppservicePlan: planName,
      });
    }
  };

  const saveTenants = async () => {
    const confirm = window.confirm('Are you sure you want to update tenants?');
    if (!confirm) return;

    try {
      incrementLoading();
      await Promise.all(
        tenants.map(async (tenant, index) => {
          if (!areTenantsEqual(tenant, originalTenants[index])) {
            await farmService.setTenant(tenant.FarmId, tenant.Id, tenant);
            showToast({
              type: 'success',
              title: `${tenant.Id} updated successfully`,
            });
          }
        }),
      );
      await loadInitialData();
    } catch (error) {
      showToast({
        type: 'error',
        title: 'Failed to save tenants',
      });
      console.error('Error saving tenants:', error);
    } finally {
      decrementLoading();
    }
  };

  if (!dialogState) return null;

  return (
    <Dialog
      open={dialogState}
      fullWidth
      fullScreen
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          width: '100vw',
          maxWidth: '100vw',
          bgcolor: 'background.default',
        },
      }}
    >
      <DialogTitle
        sx={{ px: 4, py: 3, fontWeight: 700, bgcolor: 'background.paper' }}
      >
        Farm: {farm.Id + ' - ' + farm.DisplayName}
      </DialogTitle>

      <DialogContent
        sx={{ p: '0 !important', display: 'flex', flexDirection: 'column' }}
      >
        <Paper
          elevation={0}
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              overflowX: 'auto',
              flexGrow: 1,
              minWidth: isMobile ? '600px' : 'auto',
            }}
          >
            <Table stickyHeader size="small" sx={{ width: '100%' }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      bgcolor: 'background.paper',
                      fontWeight: 600,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  ></TableCell>
                  <TableCell
                    sx={{
                      bgcolor: 'background.paper',
                      fontWeight: 600,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      textAlign: 'center',
                    }}
                  >
                    State
                  </TableCell>
                  <TableCell
                    sx={{
                      bgcolor: 'background.paper',
                      fontWeight: 600,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    Customer
                  </TableCell>
                  <TableCell
                    sx={{
                      bgcolor: 'background.paper',
                      fontWeight: 600,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    Version
                  </TableCell>
                  <TableCell
                    sx={{
                      bgcolor: 'background.paper',
                      fontWeight: 600,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    Appservice Plan
                  </TableCell>
                  <TableCell
                    sx={{
                      bgcolor: 'background.paper',
                      fontWeight: 600,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    Storage Account
                  </TableCell>
                  <TableCell
                    sx={{
                      bgcolor: 'background.paper',
                      fontWeight: 600,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    Azure Search
                  </TableCell>
                  <TableCell
                    sx={{
                      bgcolor: 'background.paper',
                      fontWeight: 600,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    Sql Server
                  </TableCell>
                  <TableCell
                    sx={{
                      bgcolor: 'background.paper',
                      fontWeight: 600,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    Replicas
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingCount > 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ py: 10 }}>
                      <Box display="flex" justifyContent="center">
                        <CircularProgress />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTenants.map((tenant) => (
                    <TenantRow
                      key={tenant.Id}
                      tenant={tenant}
                      originalTenant={originalTenants.find(
                        (t) => t.Id === tenant.Id,
                      )}
                      customers={customers}
                      appserviceplans={appserviceplans}
                      storageaccounts={storageaccounts}
                      azureSearchService={azureSearchService}
                      sqlServers={sqlServers}
                      replicaServers={replicaServers}
                      isMobile={isMobile}
                      onAppServicePlanChange={handleAppServicePlanChange}
                      onStorageAccountChange={handleStorageAccountChange}
                      onAzureSearchServiceChange={
                        handleAzureSearchServiceChange
                      }
                      onSqlServerChange={handleSqlServerChange}
                      onReplicaChange={handleReplicaChange}
                      onOpenDialog={handleOpenDialog}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      </DialogContent>

      <AppserviceSkuDialog
        selectedValue={appserviceDialogState.selectedValue}
        open={appserviceDialogState.open}
        onClose={handleCloseDialog}
      />

      <Divider />
      <DialogActions sx={{ p: 3, bgcolor: 'background.paper' }}>
        <Box
          display="flex"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box />{' '}
          {/* Spacer for centering if needed, but flex-end works. We use the right side */}
          <Box display="flex" gap={2}>
            <CustomButton
              startIcon={<Cancel />}
              onClick={() => setDialogState(false)}
              label="Close"
              color="inherit"
            />
            <CustomButton
              startIcon={<Save />}
              onClick={saveTenants}
              label="Save Changes"
            />
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default TenantTable;
